/**
 * calendarEventDefaults.ts
 * ----------------------------------------------------------------------------
 * Helpers de horário padrão para criação de eventos no Calendário Global.
 *
 * Motivação:
 *   O FullCalendar entrega `allDay: true` para cliques na visão Mês e na faixa
 *   "dia inteiro" das visões Semana/Dia. Repassar esse flag direto para o modal
 *   fazia os campos Início/Fim virarem `type="date"`, sem hora/minuto.
 *   Aqui normalizamos a seleção para um evento COM horário, mantendo o toggle
 *   "Dia inteiro" disponível no modal para quem realmente precisar.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-10 v1.0.0 - Versão inicial (horário padrão ao criar evento)
 */

/** Duração padrão de um novo evento (minutos). */
const DEFAULT_DURATION_MIN = 60;

/** Horário padrão aplicado quando a seleção não traz hora (dias futuros). */
const DEFAULT_HOUR = 9;

/** Verifica se duas datas caem no mesmo dia local. */
function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Retorna a próxima hora cheia a partir de `ref` (mínimo: hora seguinte).
 * Usado quando o usuário clica no dia de hoje — abrir às 09:00 no meio da
 * tarde seria pior do que sugerir o próximo horário disponível.
 */
function nextRoundHour(ref: Date): Date {
  const d = new Date(ref);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}

export interface ResolvedEventTimes {
  /** Início em ISO. */
  startAt: string;
  /** Fim em ISO. */
  endAt: string;
  /** Sempre `false` quando normalizamos uma seleção de dia inteiro. */
  allDay: boolean;
}

/**
 * Normaliza a seleção vinda do grid do calendário em um intervalo com horário.
 *
 * Regras:
 *  - Seleção com hora (grid Semana/Dia): mantida exatamente como veio.
 *  - Seleção de dia inteiro em 1 dia: aplica horário padrão + duração de 1h.
 *  - Seleção de vários dias: preserva o período, aplicando horário padrão no
 *    início e o mesmo horário + 1h no último dia selecionado.
 *
 * Observação sobre fuso: todos os cálculos usam métodos locais (`getHours`,
 * `setHours`), portanto respeitam o timezone do navegador (base do calendário),
 * evitando o deslocamento causado por converter datas puras via `toISOString()`.
 *
 * @param start        Início da seleção (Date do FullCalendar)
 * @param end          Fim da seleção (Date do FullCalendar; exclusivo em allDay)
 * @param isAllDaySel  Flag `allDay` devolvida pelo FullCalendar
 */
export function resolveDefaultEventTimes(
  start: Date,
  end: Date,
  isAllDaySel: boolean,
): ResolvedEventTimes {
  // Seleção já possui hora: nada a normalizar
  if (!isAllDaySel) {
    return { startAt: start.toISOString(), endAt: end.toISOString(), allDay: false };
  }

  const now = new Date();

  // Define o horário de início: próxima hora cheia se for hoje, senão 09:00
  const startAt = new Date(start);
  if (isSameLocalDay(start, now)) {
    const next = nextRoundHour(now);
    startAt.setHours(next.getHours(), 0, 0, 0);
  } else {
    startAt.setHours(DEFAULT_HOUR, 0, 0, 0);
  }

  // O `end` de uma seleção allDay é exclusivo (dia seguinte ao último dia).
  // Descontamos 1 dia para descobrir o último dia realmente selecionado.
  const lastDay = new Date(end);
  lastDay.setDate(lastDay.getDate() - 1);

  const endAt = new Date(startAt.getTime() + DEFAULT_DURATION_MIN * 60 * 1000);

  // Seleção de múltiplos dias: mantém o período, só aplica o horário
  if (!isSameLocalDay(lastDay, start) && lastDay > start) {
    endAt.setFullYear(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate());
  }

  return { startAt: startAt.toISOString(), endAt: endAt.toISOString(), allDay: false };
}
